import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export function DocumentOrder({ documents, onChanged }) {
  const [ids, setIds] = useState(documents.map((d) => d.id));
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => setIds(documents.map((d) => d.id)), [documents]);
  const byId = Object.fromEntries(documents.map((d) => [d.id, d]));

  const persist = async (next) => {
    setIds(next);
    setSaving(true);
    try {
      await api.put("/admin/documents/order", { ids: next });
      toast.success("Kolejność zapisana.");
      onChanged?.();
    } catch {
      toast.error("Nie udało się zapisać kolejności.");
      setIds(documents.map((d) => d.id));
    } finally {
      setSaving(false);
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= ids.length || from === to) return;
    const next = [...ids];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    persist(next);
  };

  const drop = (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    move(ids.indexOf(dragId), ids.indexOf(targetId));
    setDragId(null);
    setOverId(null);
  };

  return (
    <div data-testid="documents-order" className="bg-card border border-border rounded-2xl p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display font-bold text-base">Kolejność dokumentów na stronie</h3>
          <p className="text-xs text-muted-foreground mt-1">Przeciągnij wiersz (uchwyt po lewej) lub użyj strzałek. Kolejność dotyczy listy /biuro-wirtualne-warszawa/dokumenty, e-maili i czatu.</p>
        </div>
        {saving && <Loader2 size={16} className="animate-spin text-muted-foreground" />}
      </div>
      <ol className="space-y-1.5">
        {ids.map((id, i) => {
          const d = byId[id];
          if (!d) return null;
          return (
            <li
              key={id}
              data-testid={`documents-order-item-${id}`}
              draggable
              onDragStart={(e) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => { e.preventDefault(); if (overId !== id) setOverId(id); }}
              onDragLeave={() => setOverId(null)}
              onDrop={(e) => { e.preventDefault(); drop(id); }}
              onDragEnd={() => { setDragId(null); setOverId(null); }}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2 text-sm transition-colors ${dragId === id ? "opacity-50" : ""} ${overId === id && dragId !== id ? "border-brand-teal bg-brand-teal/5" : "border-border bg-background"}`}
            >
              <span className="cursor-grab active:cursor-grabbing text-muted-foreground"><GripVertical size={16} /></span>
              <span className="font-mono text-[11px] text-muted-foreground w-5">{i + 1}.</span>
              <span className={`flex-1 truncate ${d.enabled ? "" : "text-muted-foreground line-through"}`}>{d.name}</span>
              <button type="button" data-testid={`documents-order-up-${id}`} onClick={() => move(i, i - 1)} disabled={i === 0 || saving} aria-label="W górę" className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30"><ArrowUp size={14} /></button>
              <button type="button" data-testid={`documents-order-down-${id}`} onClick={() => move(i, i + 1)} disabled={i === ids.length - 1 || saving} aria-label="W dół" className="p-1.5 rounded-full hover:bg-muted disabled:opacity-30"><ArrowDown size={14} /></button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
